import React, { useEffect, useState } from 'react';
import { Table, Button, Modal, Form, Input, DatePicker, message, Popconfirm, Typography } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons';
import { getLicenses, createLicense, updateLicense, deleteLicense } from '../api';
import moment from 'moment';
moment.locale('en');

const Licenses = () => {
    const [licenses, setLicenses] = useState([]);
    const [loading, setLoading] = useState(false);
    const [pagination, setPagination] = useState({ current: 1, pageSize: 10 });
    const [modalVisible, setModalVisible] = useState(false);
    const [editing, setEditing] = useState(null);
    const [form] = Form.useForm();

    const fetchLicenses = async () => {
        setLoading(true);
        const res = await getLicenses();
        setLoading(false);
        if (res.success) {
            setLicenses(Array.isArray(res.data.data) ? res.data.data : []);
        } else {
            message.error(res.message || 'Failed to fetch licenses');
        }
    };

    useEffect(() => {
        fetchLicenses();
    }, []);

    const openModal = (record = null) => {
        setEditing(record);
        setModalVisible(true);
        if (record) {
            form.setFieldsValue({ ...record, expiry_date: moment(record.expiry_date) });
        } else {
            form.resetFields();
        }
    };

    const handleOk = async () => {
        try {
            const values = await form.validateFields();
            values.expiry_date = values.expiry_date.format('YYYY-MM-DD');
            let res;
            if (editing) {
                res = await updateLicense(editing.id, values);
            } else {
                res = await createLicense(values);
            }
            if (res.success) {
                message.success(editing ? 'License updated' : 'License created');
                setModalVisible(false);
                fetchLicenses();
            } else {
                message.error(res.message || 'Operation failed');
            }
        } catch (err) { }
    };

    const handleDelete = async (id) => {
        const res = await deleteLicense(id);
        if (res.success) {
            message.success('License deleted');
            fetchLicenses();
        } else {
            message.error(res.message || 'Delete failed');
        }
    };

    const columns = [
        {
            title: 'S.No',
            key: 'sno',
            width: 60,
            render: (_, __, index) => (pagination.current - 1) * pagination.pageSize + index + 1
        },
        { title: 'Company', dataIndex: 'company', key: 'company' },
        { title: 'Licence No', dataIndex: 'licence_no', key: 'licence_no' },
        {
            title: 'Expiry Date',
            dataIndex: 'expiry_date',
            key: 'expiry_date',
            render: d => {
                const today = moment();
                const exp = moment(d);
                let color = 'green';
                if (exp.isBefore(today, 'day')) color = 'red';
                else if (exp.diff(today, 'days') <= 30) color = 'orange';
                return <Typography.Text style={{ color }}>{exp.format('DD/MM/YYYY')}</Typography.Text>;
            }
        },
        {
            title: 'Actions',
            key: 'actions',
            width: 160,
            render: (_, record) => (
                <>
                    <Button icon={<EditOutlined />} onClick={() => openModal(record)} style={{ marginRight: 8 }} />
                    <Popconfirm title="Are you sure to delete this license?" onConfirm={() => handleDelete(record.id)} okText="Yes" cancelText="No">
                        <Button icon={<DeleteOutlined />} danger />
                    </Popconfirm>
                </>
            ),
        },
    ];

    return (
        <div>
            <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between' }}>
                <h2>Licenses</h2>
                <Button type="primary" icon={<PlusOutlined />} onClick={() => openModal()}>Add License</Button>
            </div>
            <Table
                rowKey="id"
                columns={columns}
                dataSource={licenses}
                loading={loading}
                bordered
                pagination={pagination}
                onChange={(p) => setPagination(p)}
            />
            <Modal
                title={editing ? 'Edit License' : 'Add License'}
                open={modalVisible}
                onOk={handleOk}
                onCancel={() => setModalVisible(false)}
                okText={editing ? 'Update' : 'Create'}
            >
                <Form form={form} layout="vertical">
                    <Form.Item name="company" label="Company" rules={[{ required: true, message: 'Please enter company name' }]}>
                        <Input />
                    </Form.Item>
                    <Form.Item name="licence_no" label="Licence No" rules={[{ required: true, message: 'Please enter licence number' }]}>
                        <Input />
                    </Form.Item>
                    <Form.Item name="expiry_date" label="Expiry Date" rules={[
                        { required: true, message: 'Please select expiry date' },
                        {
                            validator: (_, value) => {
                                if (value && moment(value).isBefore(moment().startOf('day'))) {
                                    return Promise.reject(new Error('Expiry date cannot be in the past'));
                                }
                                return Promise.resolve();
                            }
                        }
                    ]}>
                        <DatePicker 
                            style={{ width: '100%' }} 
                            format="DD/MM/YYYY" 
                            disabledDate={(current) => current && current.isBefore(moment().startOf('day'))}
                        />
                    </Form.Item>
                </Form>
            </Modal>
        </div>
    );
};

export default Licenses;
